import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Upload,
  Radio,
  notification,
  Checkbox,
  TimePicker,
  Switch,
} from "antd";
import {
  MachineItem,
  ProductCardItem,
  useCreateTaskMutation,
  useGetAllProductCardsQuery,
  useUploadFileMutation,
  useUploadImageMutation,
} from "shared/api";
import { useEffect, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  machinesData: MachineItem[] | undefined;
  refetchExecutions: () => void | undefined;
  suppliersCompaniesFormatted:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  open,
  onClose,
  transformedUsers,
  machinesData,
  refetchExecutions,
  suppliersCompaniesFormatted,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const fileDocRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedIconFile, setUploadedIconFile] = useState<File | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [repeatEvery, setRepeatEvery] = useState<string | null>(null);

  const periodicity = Form.useWatch("periodicity", form);
  const isRecurrent = Form.useWatch("is_recurrent", form);
  const selectedTime = Form.useWatch("time", form);
  const enabled = Form.useWatch("enabled", form);

  useEffect(() => {
    setRepeatEvery(periodicity || null);
  }, [periodicity]);

  const [uploadFile] = useUploadFileMutation();
  const [uploadImage] = useUploadImageMutation();
  const [createTask, { isLoading }] = useCreateTaskMutation();

  const { data: productCardsData } = useGetAllProductCardsQuery({}) as {
    data: ProductCardItem[] | undefined;
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileName(null);
      setPreviewUrl(null);
      setUploadedIconFile(null);
      setAttachedFiles([]);
    }
  }, [open, form]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleFinish = async (values: any) => {
    try {
      const payload = {
        name: values.name,
        periodicity: values.periodicity || null,
        is_recurrent: values.is_recurrent || false,
        time: values.time ? values.time.format("HH:mm:ss") : null,
        start_periodicity: values.start_periodicity
          ? dayjs(values.start_periodicity)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        end_periodicity: values.end_periodicity
          ? dayjs(values.end_periodicity).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        machines: values.machines || [],
        product_cards: values.product_cards || [],
        enabled: values.enabled,
        priority: values.priority,
        users: values.user || [],
        description: showDescription ? values.description : null,
        companies: values.companies,
      };

      const { id: taskId } = await createTask(payload).unwrap();

      if (uploadedIconFile && taskId) {
        await uploadImage({ id: taskId, file: uploadedIconFile }).unwrap();
      }

      if (attachedFiles.length > 0 && taskId) {
        await Promise.all(
          attachedFiles.map((file) => uploadFile({ id: taskId, file }).unwrap())
        );
      }

      refetchExecutions();

      api.success({
        message: "Task Created",
        description: "The task was successfully created.",
        placement: "topRight",
      });

      setShowDescription(false);
      setAttachedFiles([]);
      onClose();
    } catch (err) {
      api.error({
        message: "Task Creation Failed",
        description: "Something went wrong while creating the task.",
        placement: "topRight",
      });

      // eslint-disable-next-line no-console
      console.error("Failed to create task", err);
    } finally {
      onClose();
    }
  };

  const renderFileUploader = () => (
    <Upload
      accept="image/*"
      showUploadList={{
        showPreviewIcon: false,
        showRemoveIcon: true,
      }}
      fileList={
        uploadedIconFile
          ? [
              {
                uid: "-1",
                name: fileName || "uploaded_file.png",
                status: "done",
                thumbUrl: previewUrl || "",
              },
            ]
          : []
      }
      beforeUpload={(file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          setPreviewUrl(base64);
          setUploadedIconFile(file);
          setFileName(file.name);
        };
        reader.readAsDataURL(file);
        return false;
      }}
      onRemove={() => {
        setPreviewUrl(null);
        setUploadedIconFile(null);
        setFileName(null);
      }}
    >
      {!uploadedIconFile ? (
        <Button className="w-8 h-8 rounded-full p-0">
          <PlusOutlined className="p-0 mb-1" />
        </Button>
      ) : (
        previewUrl && (
          <img
            src={previewUrl}
            alt={t("taskHub.modal.preview") || ""}
            className="w-8 h-8 rounded-full object-cover border"
          />
        )
      )}
    </Upload>
  );

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        destroyOnClose
        title={
          <div className="flex items-center justify-between w-[95%] mb-3">
            <span>{t("taskHub.modal.addTask") || ""}</span>
            <div className="flex gap-2 items-center min-w-[130px] justify-end">
              <span className="text-sm">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onChange={(checked) => form.setFieldValue("enabled", checked)}
                style={{
                  backgroundColor: enabled ? "limegreen" : "tomato",
                }}
              />
            </div>
          </div>
        }
        width={720}
        okButtonProps={{
          style: {
            backgroundColor: themeColor,
            color: "#fff",
            borderColor: "#1677ff",
          },
        }}
        okText={t("taskHub.modal.ok") || ""}
        cancelText={t("taskHub.modal.cancel") || ""}
      >
        <Form
          initialValues={{
            enabled: true,
          }}
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <div>
            <div className="grid grid-cols-2 gap-x-4">
              <div className="col-span-2 flex items-center justify-between">
                <h3 className="text-base font-semibold mb-2">
                  {t("taskHub.modal.taskIcon") || ""}
                </h3>
                {renderFileUploader()}
              </div>

              <Form.Item
                name="name"
                label={t("taskHub.modal.name") || ""}
                rules={[{ required: true }]}
              >
                <Input placeholder={t("taskHub.modal.enterTaskName") || ""} />
              </Form.Item>

              <Form.Item name="user" label={t("taskHub.modal.user") || ""}>
                <Select
                  mode="multiple"
                  showSearch
                  options={transformedUsers}
                  optionFilterProp="label"
                  placeholder={t("taskHub.modal.selectUser") || ""}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">
              {t("taskHub.modal.periodicity") || ""}
            </h3>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="is_recurrent" valuePropName="checked">
                <Checkbox>{t("taskHub.modal.enableRecurrence") || ""}</Checkbox>
              </Form.Item>

              <Form.Item
                name="priority"
                label={t("taskHub.modal.priority") || ""}
              >
                <Select placeholder={t("taskHub.modal.selectPriority") || ""}>
                  <Select.Option value={1}>1</Select.Option>
                  <Select.Option value={2}>2</Select.Option>
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={4}>4</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item
                name="start_periodicity"
                label={t("taskHub.modal.startDate") || ""}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                  showTime
                  disabledDate={(date) => date && date.isBefore(dayjs(), "day")}
                />
              </Form.Item>

              {isRecurrent && (
                <>
                  <Form.Item
                    name="end_periodicity"
                    label={t("taskHub.modal.endDate") || ""}
                  >
                    <DatePicker
                      format="YYYY-MM-DD HH:mm"
                      showTime
                      style={{ width: "100%" }}
                      disabledDate={(current) => {
                        const start = form.getFieldValue("start_periodicity");
                        if (!start || !current) return false;
                        return current.isBefore(dayjs(start), "day");
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="periodicity"
                    label={t("taskHub.modal.repeatEvery") || ""}
                  >
                    <Radio.Group className="flex flex-col">
                      <Radio value="daily">
                        {t("taskHub.modal.daily") || ""}
                      </Radio>
                      <Radio value="weekly">
                        {t("taskHub.modal.weekly") || ""}
                      </Radio>
                      <Radio value="monthly">
                        {t("taskHub.modal.monthly") || ""}
                      </Radio>
                      <Radio value="yearly">
                        {t("taskHub.modal.yearly") || ""}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </>
              )}

              {repeatEvery && (
                <Form.Item name="time" label={t("taskHub.modal.time") || ""}>
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    value={selectedTime}
                  />
                </Form.Item>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">
              {t("taskHub.modal.associations") || ""}
            </h3>
            <div className="grid grid-cols-1 gap-x-4">
              <div className="w-full flex flex-col gap-4">
                <div className="flex w-full gap-4">
                  <Form.Item
                    name="machines"
                    label={t("taskHub.modal.machines") || ""}
                    className="flex-1 !mb-0"
                  >
                    <Select
                      placeholder={t("taskHub.modal.selectMachines") || ""}
                      mode="multiple"
                      options={
                        machinesData?.map((m) => ({
                          label: m.name,
                          value: m.id,
                        })) || []
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="product_cards"
                    label={t("taskHub.modal.productCards") || ""}
                    className="flex-1 truncate overflow-x-hidden !mb-0"
                  >
                    <Select
                      placeholder={t("taskHub.modal.selectProductCards") || ""}
                      showSearch
                      optionFilterProp="label"
                      mode="multiple"
                      options={
                        productCardsData?.map((p) => ({
                          label: p.name,
                          value: p.id,
                        })) || []
                      }
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-2 gap-x-4">
                  <Form.Item
                    name="companies"
                    label={t("taskHub.modal.suppliers") || ""}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      options={suppliersCompaniesFormatted}
                      optionFilterProp="label"
                      placeholder={t("taskHub.modal.selectSupplier") || ""}
                      filterOption={(input, option) =>
                        (option?.label as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="flex gap-2 flex-col">
                <div className="flex gap-x-2">
                  <Form.Item className="!mb-0">
                    <Button
                      type="primary"
                      style={{ backgroundColor: themeColor }}
                      className="!mb-0"
                      onClick={() => setShowDescription((prev) => !prev)}
                    >
                      {showDescription
                        ? t("taskHub.modal.clearDescription") || ""
                        : t("taskHub.modal.addDescription") || ""}
                    </Button>
                  </Form.Item>

                  <Form.Item className="!mb-0">
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        multiple
                        onChange={handleAttachFiles}
                        ref={fileDocRef}
                        className="!hidden"
                      />
                      <Button
                        type="primary"
                        style={{ backgroundColor: themeColor }}
                        className="!mb-0"
                        onClick={() => fileDocRef.current?.click()}
                      >
                        {t("taskHub.modal.attachFiles") || ""}
                      </Button>
                    </div>
                  </Form.Item>
                </div>

                <div>
                  {showDescription && (
                    <Form.Item
                      name="description"
                      label={t("taskHub.modal.description") || ""}
                      className="col-span-2"
                    >
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  )}

                  {attachedFiles.length > 0 && (
                    <ul className="mt-2 space-y-2">
                      {attachedFiles.map((file, idx) => {
                        return (
                          <li
                            key={idx}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded border"
                          >
                            <span
                              className="truncate max-w-[80%]"
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            <Button
                              style={{ backgroundColor: themeColor }}
                              danger
                              type="text"
                              size="small"
                              onClick={() => {
                                setAttachedFiles((prev) =>
                                  prev.filter(
                                    (_, fileIndex) => fileIndex !== idx
                                  )
                                );
                              }}
                            >
                              <span className="!p-0">
                                {t("taskHub.modal.delete") || ""}
                              </span>
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Form.Item name="enabled" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
